import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { propertyApi } from '../services';
import type { PropertyFile } from '../services';
import { useToast } from '../contexts/ToastContext';
import './PropertyFiles.css';

// SVG Icons matching screenshot style
export const PdfIcon = () => (
  <svg
    width="34"
    height="42"
    viewBox="0 0 34 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 0C0.895431 0 0 0.895431 0 2V40C0 41.1046 0.895431 42 2 42H32C33.1046 42 34 41.1046 34 40V12L22 0H2Z"
      fill="white"
    />
    <path d="M22 0V12H34L22 0Z" fill="#F3F1FF" />
    <rect x="5" y="24" width="24" height="12" rx="2" fill="#F13050" />
    <path
      d="M10.5 28.5V31.5H9.5V28.5H10.5ZM10.5 28.5H11.5V29.5H10.5V28.5ZM14.5 28.5V31.5H13.5V28.5H14.5ZM14.5 28.5H15.5V29.5H14.5V28.5ZM18.5 28.5V31.5H17.5V28.5H18.5ZM18.5 28.5H19.5V29.5H18.5V28.5Z"
      fill="white"
    />
    <text
      x="9"
      y="33"
      fill="white"
      fontSize="7"
      fontFamily="Inter"
      fontWeight="bold"
    >
      PDF
    </text>
  </svg>
);

export const ImageIcon = () => (
  <svg
    width="34"
    height="42"
    viewBox="0 0 34 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 0C0.895431 0 0 0.895431 0 2V40C0 41.1046 0.895431 42 2 42H32C33.1046 42 34 41.1046 34 40V12L22 0H2Z"
      fill="white"
    />
    <path d="M22 0V12H34L22 0Z" fill="#F3F1FF" />
    <rect x="5" y="24" width="24" height="12" rx="2" fill="#571AFF" />
    <text
      x="9"
      y="33"
      fill="white"
      fontSize="7"
      fontFamily="Inter"
      fontWeight="bold"
    >
      IMG
    </text>
  </svg>
);

export const DocIcon = () => (
  <svg
    width="34"
    height="42"
    viewBox="0 0 34 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 0C0.895431 0 0 0.895431 0 2V40C0 41.1046 0.895431 42 2 42H32C33.1046 42 34 41.1046 34 40V12L22 0H2Z"
      fill="white"
    />
    <path d="M22 0V12H34L22 0Z" fill="#F3F1FF" />
    <rect x="5" y="24" width="24" height="12" rx="2" fill="#2D9CDB" />
    <text
      x="9"
      y="33"
      fill="white"
      fontSize="7"
      fontFamily="Inter"
      fontWeight="bold"
    >
      DOC
    </text>
  </svg>
);

const UploadIcon = () => (
  <svg
    width="24"
    height="30"
    viewBox="0 0 24 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 0C0.895431 0 0 0.895431 0 2V28C0 29.1046 0.895431 30 2 30H22C23.1046 30 24 29.1046 24 28V8.57143L15.4286 0H2Z"
      fill="none"
      stroke="#A7A3E2"
      strokeWidth="2"
    />
    <path
      d="M12 22V12M12 12L9 15M12 12L15 15"
      stroke="#A7A3E2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeaderIcon = () => (
  <svg
    width="18"
    height="22"
    viewBox="0 0 18 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.5 1.5V20.5H16.5V7.5L10.5 1.5H1.5Z"
      stroke="#A7A3E2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 1.5V7.5H16.5"
      stroke="#A7A3E2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface PropertyFilesProps {
  propertyId: string;
}

const PropertyFiles: React.FC<PropertyFilesProps> = ({ propertyId }) => {
  const [files, setFiles] = useState<PropertyFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [deleteModalFile, setDeleteModalFile] = useState<PropertyFile | null>(
    null,
  );
  const { showSuccess, showError } = useToast();

  const fetchFiles = useCallback(async () => {
    if (!propertyId || propertyId === 'new' || !propertyId) {
      setLoading(false);
      return;
    }

    try {
      const fetchedFiles = await propertyApi.getFiles(propertyId);
      setFiles(fetchedFiles);
    } catch (error: any) {
      showError('Failed to load files');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [propertyId, showError]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!propertyId || propertyId === 'new') {
        showError(
          'Bitte speichern Sie die Immobilie zuerst, bevor Sie Dateien hochladen.',
        );
        return;
      }

      setUploading(true);
      try {
        for (const file of acceptedFiles) {
          await propertyApi.uploadFile(propertyId, file);
        }
        showSuccess(
          `${acceptedFiles.length} Datei(en) erfolgreich hochgeladen.`,
        );
        await fetchFiles();
      } catch (error: any) {
        showError('Fehler beim Hochladen der Dateien.');
        console.error(error);
      } finally {
        setUploading(false);
      }
    },
    [propertyId, fetchFiles, showSuccess, showError],
  );

  const openDeleteModal = (e: React.MouseEvent, file: PropertyFile) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModalFile(file);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalFile) return;

    try {
      await propertyApi.deleteFile(propertyId, deleteModalFile.id);
      showSuccess('Datei erfolgreich gelöscht.');
      setFiles((prev) => prev.filter((f) => f.id !== deleteModalFile.id));
    } catch (error: any) {
      showError('Fehler beim Löschen der Datei.');
      console.error(error);
    } finally {
      setDeleteModalFile(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
  });

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <PdfIcon />;
    if (mimeType.includes('image')) return <ImageIcon />;
    return <DocIcon />;
  };

  return (
    <div className="property-files-container">
      <div className="property-files-header">
        <h3>Dateien & Unterlagen</h3>
        <div className="property-files-header-icon">
          <HeaderIcon />
        </div>
      </div>

      <div className="property-files-content">
        {!loading && files.length > 0 && (
          <div className="property-files-list">
            {files.map((file) => (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="property-file-item"
                key={file.id}
              >
                <button
                  className="property-file-delete-btn"
                  onClick={(e) => openDeleteModal(e, file)}
                  title="Löschen"
                >
                  ×
                </button>
                <div className="property-file-icon">
                  {getFileIcon(file.mimeType)}
                </div>
                <div className="property-file-name" title={file.filename}>
                  {file.filename}
                </div>
              </a>
            ))}
          </div>
        )}

        {deleteModalFile &&
          createPortal(
            <div
              className="confirm-modal-overlay"
              onClick={() => setDeleteModalFile(null)}
            >
              <div
                className="confirm-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Datei unwiderruflich löschen</h3>
                <p>
                  Möchten Sie die Datei "{deleteModalFile.filename}" wirklich
                  löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.
                </p>
                <div className="confirm-modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setDeleteModalFile(null)}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="btn-confirm"
                    onClick={handleConfirmDelete}
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        <div
          {...getRootProps()}
          className={`property-files-dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        >
          <input
            {...getInputProps()}
            disabled={uploading || propertyId === 'new'}
          />
          <div className="property-files-dropzone-icon">
            <UploadIcon />
          </div>
          <div className="property-files-dropzone-text">
            {uploading ? (
              'Wird hochgeladen...'
            ) : (
              <>
                Dateien hier ablegen oder <strong>Dateien hochladen</strong>
              </>
            )}
          </div>
          <div className="property-files-dropzone-formats">
            .pdf .png .jpg .docx .xlsx
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyFiles;
